import { useCallback, useMemo, useEffect, useState } from 'react';
import { useOrderStore, type OrderStatus, type OrderTag } from '../stores/useOrderStore';
import { useUrlState } from './useUrlState';

export interface JamakeDataHook {
  // 데이터
  orders: any[];
  currentPageOrders: any[];
  
  // 페이지네이션
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    startIndex: number;
    endIndex: number;
  };
  
  // 필터
  filters: {
    search: string;
    dateRange: { start: string; end: string };
    statuses: OrderStatus[];
    tags: OrderTag[];
  };

  // 선택 상태
  selection: {
    selectedIds: string[];
    selectedOrders: any[];
  };
  
  // 액션
  actions: {
    setSearch: (search: string) => void;
    setDateRange: (range: { start?: string; end?: string }) => void;
    setStatuses: (statuses: OrderStatus[]) => void;
    setTags: (tags: OrderTag[]) => void;
    resetFilters: () => void;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    toggleSelection: (orderId: string) => void;
    clearSelection: () => void;
  };
  
  // 계산된 값들
  computed: {
    activeFiltersCount: number;
    isEmpty: boolean;
    isLoading: boolean;
    selectedCount: number;
  };
}

export const useJamakeData = (): JamakeDataHook => {
  const {
    orders: allOrders,
    setFilters: setStoreFilters,
    setCurrentPage: setStoreCurrentPage,
    setPageSize: setStorePageSize,
  } = useOrderStore();

  // URL 상태 관리
  const {
    filters: urlFilters,
    pagination: urlPagination,
    updatePage,
    updatePageSize,
    updateFilters,
    resetFilters: resetUrlFilters,
  } = useUrlState();

  // 선택 상태 관리
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // URL 상태를 스토어에 동기화
  useEffect(() => {
    setStoreFilters(urlFilters);
    setStoreCurrentPage(urlPagination.page);
    setStorePageSize(urlPagination.pageSize);
  }, [urlFilters, urlPagination, setStoreFilters, setStoreCurrentPage, setStorePageSize]);

  // 필터링된 주문 계산
  const filteredOrders = useMemo(() => {
    return allOrders.filter(order => {
      // 텍스트 검색
      if (urlFilters.search) {
        const searchTerm = urlFilters.search.toLowerCase();
        const matchesSearch = 
          order.customer.name.toLowerCase().includes(searchTerm) ||
          order.customer.email.toLowerCase().includes(searchTerm) ||
          order.title.toLowerCase().includes(searchTerm) ||
          order.id.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // 날짜 범위 필터
      if (urlFilters.dateRange.start || urlFilters.dateRange.end) {
        const orderDate = order.registeredAt.replace(/\//g, '-'); // YYYY/MM/DD -> YYYY-MM-DD
        if (urlFilters.dateRange.start && orderDate < urlFilters.dateRange.start) return false;
        if (urlFilters.dateRange.end && orderDate > urlFilters.dateRange.end) return false;
      }

      // 상태 필터
      if (urlFilters.statuses.length > 0 && !urlFilters.statuses.includes(order.status)) {
        return false;
      }

      // 태그 필터
      if (urlFilters.tags.length > 0) {
        const hasMatchingTag = urlFilters.tags.some(tag => order.tags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      return true;
    });
  }, [allOrders, urlFilters]);

  // 총 페이지 수 계산
  const totalPages = Math.ceil(filteredOrders.length / urlPagination.pageSize);
  const totalCount = filteredOrders.length;

  // 페이지네이션 계산
  const pagination = useMemo(() => {
    const startIndex = (urlPagination.page - 1) * urlPagination.pageSize;
    const endIndex = startIndex + urlPagination.pageSize;
    
    return {
      currentPage: urlPagination.page,
      totalPages,
      pageSize: urlPagination.pageSize,
      totalCount,
      startIndex,
      endIndex
    };
  }, [urlPagination.page, urlPagination.pageSize, totalPages, totalCount]);

  // 현재 페이지 데이터
  const currentPageOrders = useMemo(() => {
    const { startIndex, endIndex } = pagination;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, pagination]);

  // 필터 액션들 - URL 기반으로 변경
  const setSearch = useCallback((search: string) => {
    updateFilters({ search });
  }, [updateFilters]);

  const setDateRange = useCallback((range: { start?: string; end?: string }) => {
    const newDateRange = { 
      ...urlFilters.dateRange,
      ...range
    };
    updateFilters({ 
      dateRange: newDateRange
    });
  }, [updateFilters, urlFilters.dateRange]);

  const setStatuses = useCallback((statuses: OrderStatus[]) => {
    updateFilters({ statuses });
  }, [updateFilters]);

  const setTags = useCallback((tags: OrderTag[]) => {
    updateFilters({ tags });
  }, [updateFilters]);

  // 선택 관련 액션들
  const toggleSelection = useCallback((orderId: string) => {
    console.log('toggleSelection called for:', orderId);
    setSelectedIds(prev => {
      const newSelection = prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId];
      console.log('Selection updated from', prev, 'to', newSelection);
      return newSelection;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // 선택된 주문들 계산 (전체 주문에서 선택)
  const selectedOrders = useMemo(() => {
    return allOrders.filter(order => selectedIds.includes(order.id));
  }, [allOrders, selectedIds]);

  // 계산된 값들
  const computed = useMemo(() => {
    const activeFiltersCount = 
      (urlFilters.search ? 1 : 0) +
      (urlFilters.dateRange.start || urlFilters.dateRange.end ? 1 : 0) +
      urlFilters.statuses.length +
      urlFilters.tags.length;

    return {
      activeFiltersCount,
      isEmpty: filteredOrders.length === 0,
      isLoading: false, // 향후 API 호출시 사용
      selectedCount: selectedIds.length
    };
  }, [urlFilters, filteredOrders.length, selectedIds.length]);

  return {
    orders: filteredOrders,
    currentPageOrders,
    pagination,
    filters: urlFilters,
    selection: {
      selectedIds,
      selectedOrders
    },
    actions: {
      setSearch,
      setDateRange,
      setStatuses,
      setTags,
      resetFilters: resetUrlFilters,
      setCurrentPage: updatePage,
      setPageSize: updatePageSize,
      toggleSelection,
      clearSelection,
    },
    computed
  };
};
